'use client';

import { useState, useEffect } from 'react';

import {
  ChevronDown,
  Download,
  Ellipsis,
  MoreHorizontal,
  Plus,
  Share,
  X,
} from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';

// Note: X and Button are kept for the commented-out banner code below

function usePwaInstall() {
  const [isIOS, setIsIOS] = useState(false);
  const [needsMoreStep, setNeedsMoreStep] = useState(false);
  const [isCompactMode, setIsCompactMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const ua = window.navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
    const isMobileDevice =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isInStandaloneMode =
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in window.navigator &&
        (window.navigator as Navigator & { standalone: boolean }).standalone);

    // Feature detection for iOS 26+ (Safari 26+)
    // iOS 26+ has a new share sheet UI that requires tapping "More" to find "Add to Home Screen"
    // iOS 18 and lower show "Add to Home Screen" directly in the share sheet
    // Using CSS anchor positioning (anchor-name) which was introduced in Safari 26.0
    // Reference: https://webkit.org/blog/17333/webkit-features-in-safari-26-0/
    const isAtLeastiOS26 =
      isIOSDevice &&
      typeof CSS !== 'undefined' &&
      CSS.supports('anchor-name', '--test');

    // iOS 26 has three tab bar modes: Compact, Bottom, Top
    // Compact mode requires tapping ••• first to reveal the toolbar
    // There's no reliable API to detect which mode the user has selected
    // So we show the ••• step for all iOS 26+ users (they can skip if not in Compact mode)
    const compactMode = isAtLeastiOS26;

    setIsIOS(isIOSDevice);
    setNeedsMoreStep(isAtLeastiOS26);
    setIsCompactMode(compactMode);
    setIsMobile(isMobileDevice);
    setIsStandalone(isInStandaloneMode);
  }, []);

  return {
    isIOS,
    needsMoreStep,
    isCompactMode,
    isMobile,
    isStandalone,
    showPrompt: !isStandalone && isMobile,
  };
}

function PwaInstallModal({
  open,
  onClose,
  isIOS,
  needsMoreStep,
  isCompactMode,
}: {
  open: boolean;
  onClose: () => void;
  isIOS: boolean;
  needsMoreStep: boolean;
  isCompactMode: boolean;
}) {
  const { t } = useTranslation();
  // Calculate step numbers based on mode
  const getStepNumber = (baseStep: number) => {
    if (isCompactMode) return baseStep + 1;
    return baseStep;
  };
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg animate-in slide-in-from-bottom duration-300">
        <div className="rounded-t-3xl bg-zinc-900 px-5 pb-6 pt-4 text-white">
          {/* Header */}
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-medium text-zinc-300">
              {t('common:pwaInstall.title')}
            </h2>
            <button
              onClick={onClose}
              className="rounded-full p-1.5 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
              aria-label={t('common:close')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Instructions */}
          {isIOS ? (
            <ol className="space-y-4">
              {/* Step 1: Compact mode - tap ••• to reveal toolbar */}
              {isCompactMode && (
                <li>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-zinc-400">1.</span>
                    <span className="text-sm text-zinc-300">{t('common:pwaInstall.tap')}</span>
                    <span className="inline-flex items-center rounded-lg bg-zinc-800 px-2.5 py-1.5">
                      <Ellipsis className="h-4 w-4 text-zinc-400" />
                    </span>
                    <span className="text-sm text-zinc-300">
                      {t('common:pwaInstall.revealToolbar')}
                    </span>
                    <span className="text-xs text-zinc-500">{t('common:pwaInstall.optional')}</span>
                  </div>
                </li>
              )}

              {/* Share button step */}
              <li className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-zinc-400">
                    {getStepNumber(1)}.
                  </span>
                  <span className="text-sm text-zinc-300">{t('common:pwaInstall.press')}</span>
                  <span className="inline-flex items-center rounded-lg bg-zinc-800 px-2.5 py-1.5">
                    <Share className="h-4 w-4 text-blue-400" />
                  </span>
                  <span className="text-sm text-zinc-300">
                    {t('common:pwaInstall.inToolbar')}
                  </span>
                </div>
                <div className="ml-5 overflow-hidden rounded-xl">
                  <img
                    src="/images/pwa/share_button.jpg"
                    alt={t('common:pwaInstall.shareButtonAlt')}
                    className="w-full"
                  />
                </div>
              </li>

              {/* More button step (only for iOS 26+) */}
              {needsMoreStep && (
                <li className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-zinc-400">
                      {getStepNumber(2)}.
                    </span>
                    <span className="text-sm text-zinc-300">{t('common:pwaInstall.tap')}</span>
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-800 px-2.5 py-1.5">
                      <MoreHorizontal className="h-4 w-4 text-zinc-400" />
                      <span className="text-sm text-zinc-300">{t('common:pwaInstall.more')}</span>
                    </span>
                    <span className="text-sm text-zinc-300">
                      {t('common:pwaInstall.seeAllActions')}
                    </span>
                  </div>
                  <div className="ml-5 overflow-hidden rounded-xl">
                    <img
                      src="/images/pwa/more_button.jpg"
                      alt={t('common:pwaInstall.moreButtonAlt')}
                      className="w-full"
                    />
                  </div>
                </li>
              )}

              {/* Add to Home Screen step */}
              <li className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-zinc-400">
                    {getStepNumber(needsMoreStep ? 3 : 2)}.
                  </span>
                  <span className="text-sm text-zinc-300">{t('common:pwaInstall.select')}</span>
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-800 px-2.5 py-1.5">
                    <Plus className="h-4 w-4 text-zinc-400" />
                    <span className="text-sm text-zinc-300">
                      {t('common:pwaInstall.addToHomeScreen')}
                    </span>
                  </span>
                </div>
                <div className="ml-5 overflow-hidden rounded-xl">
                  <img
                    src="/images/pwa/add_screen.jpg"
                    alt={t('common:pwaInstall.addScreenAlt')}
                    className="w-full"
                  />
                </div>
              </li>
            </ol>
          ) : (
            <ol className="space-y-5">
              <li className="flex items-center gap-3">
                <span className="text-sm font-semibold text-zinc-400">1.</span>
                <span className="text-sm text-zinc-300">{t('common:pwaInstall.tapMenu')}</span>
                <span className="inline-flex items-center rounded-lg bg-zinc-800 px-2.5 py-1.5">
                  <MoreHorizontal className="h-4 w-4 text-zinc-400" />
                </span>
                <span className="text-sm text-zinc-300">{t('common:pwaInstall.inYourBrowser')}</span>
              </li>

              <li className="flex items-start gap-3">
                <span className="text-sm font-semibold text-zinc-400">2.</span>
                <span className="text-sm text-zinc-300">
                  {t('common:pwaInstall.androidSelect')}
                </span>
              </li>
            </ol>
          )}

          {/* Bouncing arrow pointing to browser toolbar */}
          {isIOS && (
            <div className="mt-6 flex justify-center">
              <div className="animate-bounce">
                <ChevronDown className="h-8 w-8 text-blue-400" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function PwaInstallPrompt() {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const { isIOS, needsMoreStep, isCompactMode, showPrompt } = usePwaInstall();

  if (!showPrompt) {
    return null;
  }

  return (
    <>
      {/* Floating Download Button */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 left-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 active:scale-95"
        aria-label={t('common:pwaInstall.installAppAriaLabel')}
      >
        <Download className="h-6 w-6" />
      </button>

      <PwaInstallModal
        open={showModal}
        onClose={() => setShowModal(false)}
        isIOS={isIOS}
        needsMoreStep={needsMoreStep}
        isCompactMode={isCompactMode}
      />
    </>
  );
}

export function PwaInstallBanner() {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const { isIOS, needsMoreStep, isCompactMode, showPrompt } = usePwaInstall();

  if (!showPrompt) {
    return null;
  }

  // Show only the floating download button (banner commented out for now)
  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 left-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 active:scale-95"
        aria-label={t('common:pwaInstall.installAppAriaLabel')}
      >
        <Download className="h-6 w-6" />
      </button>

      <PwaInstallModal
        open={showModal}
        onClose={() => setShowModal(false)}
        isIOS={isIOS}
        needsMoreStep={needsMoreStep}
        isCompactMode={isCompactMode}
      />
    </>
  );

  /* Save Time Banner - Commented out for PWA install enhancement
  const [showBanner, setShowBanner] = useState(true);

  // Check if already dismissed
  if (typeof window !== 'undefined' && showBanner) {
    const wasDismissed = sessionStorage.getItem('pwa-banner-dismissed');
    if (wasDismissed === 'true') {
      setShowBanner(false);
    }
  }

  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('pwa-banner-dismissed', 'true');
    }
  }, []);

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-50 border-t bg-background p-4 shadow-lg sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-sm sm:rounded-xl sm:border">
        <button
          onClick={handleDismiss}
          className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="pr-6">
          <h3 className="text-lg font-semibold">Save Yourself Time!</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Use the download button on the lower left to save this site as an
            app on your home screen for quick and easy access.
          </p>
        </div>

        <div className="mt-4 flex gap-3">
          <Button
            onClick={() => setShowModal(true)}
            size="sm"
            className="flex-1"
          >
            <Download className="mr-2 h-4 w-4" />
            Install Now
          </Button>
        </div>
      </div>

      <PwaInstallModal
        open={showModal}
        onClose={() => setShowModal(false)}
        isIOS={isIOS}
      />
    </>
  );
  */
}
